import React, { useState } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import './Waitlist.scss';

import UserWaitlistDecisionModal from './UserWaitlistDecisionModal';
import WaitlistData from './WaitlistData';
import WaitlistUser from './WaitlistUser';
import WaitlistControls from './WaitlistControls';
import WaitlistBlockedCountriesModal from './WaitlistBlockedCountriesModal';
import SignupCountryBlockBypassCodesModal from './SignupCountryBlockBypassCodesModal';
import WaitlistShimmerRow from './WaitlistShimmerRow';
import Tooltip from '../General/Tooltip';

import { updateWaitlistUser as updateWaitlistUserAPI } from '../../api/waitlist';
import * as waitlistAPI from '../../api/waitlist';

const COLUMNS = [
  { key: 'createdAt', label: 'Created At', className: 'created', asc: 'Oldest first', desc: 'Newest first' },
  { key: 'name', label: 'Name', className: 'name', asc: 'A to Z', desc: 'Z to A' },
  { key: 'email', label: 'Email', className: 'email', asc: 'A to Z', desc: 'Z to A' },
  { key: 'links', label: 'Links', className: 'links', asc: 'Fewest links first', desc: 'Most links first' },
  { key: 'largestFollowing', label: 'Max Follower Count', className: 'followers', asc: 'Smallest audience first', desc: 'Largest audience first' },
  { key: 'referred', label: 'Referred', className: 'referred', asc: 'Referrer A to Z', desc: 'Referrer Z to A' },
  { key: 'location', label: 'Location', className: 'location', asc: 'Country A to Z', desc: 'Country Z to A' },
  { key: 'status', label: 'Status', className: 'status', asc: 'Pending first', desc: 'Rejected first' }
];

const SORT_DIRECTIONS = [
  { direction: 'asc', arrow: '↑' },
  { direction: 'desc', arrow: '↓' }
];

const Waitlist = () => {
  const [waitlist, setWaitlist] = useState([]);

  const limit = 500;
  const [offset, setOffset] = useState(0);
  const [curSearchValue, setCurSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const resetSearch = () => setCurSearchValue('');

  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [isCountryBlocksModalOpen, setIsCountryBlocksModalOpen] = useState(false);
  const [isBypassCodesModalOpen, setIsBypassCodesModalOpen] = useState(false);
  const closeModals = () => setSelectedApplicationId(null);

  const fetchMoreWaitlistUsers = () => {
    if (isLoading) return cogoToast.error('Cannot fetch more users while loading.');

    // WaitlistData will handle the API call
    setOffset(offset + limit);
  };

  const updateWaitlistUser = async (waitlistUser, updates) => {
    const prevRow = waitlist.find(u => u.id === waitlistUser.id);
    setWaitlist(prev => prev.map(u => (u.id === waitlistUser.id ? { ...u, ...updates } : u)));

    try {
      const updatedUser = await updateWaitlistUserAPI(waitlistUser, updates);
      return updatedUser;
    } catch (error) {
      // Revert the optimistic update so a failed accept (e.g. code already in use) can be retried with a new code.
      if (prevRow) setWaitlist(prev => prev.map(u => (u.id === waitlistUser.id ? prevRow : u)));
      cogoToast.error(error || 'Unknown error updating user.');
      console.error(error);
      return null;
    }
  };

  const acceptUser = async (waitlistUser, options) => {
    try {
      const newUser = await updateWaitlistUser(waitlistUser, options);
      return newUser;
    } catch (error) {
      cogoToast.error(error || 'Unknown error accepting user.');
      console.error(error);
      return null;
    }
  };

  const rejectWaitlistApplication = async waitlistApplication => {
    return confirmAlert({
      title: 'Are you sure?',
      message: `Are you sure you want to reject ${waitlistApplication.name}'s application?`,
      buttons: [
        {
          label: 'Reject With Email',
          onClick: () => {
            updateWaitlistUser(waitlistApplication, { isAccepted: false, isRejected: true }).then(
              () => closeModals(),
              error => cogoToast.error(error)
            );
          }
        },
        {
          label: 'Reject Without Email',
          onClick: () => {
            updateWaitlistUser(waitlistApplication, { isAccepted: false, isRejected: true, handledOffline: true }).then(
              () => closeModals(),
              error => cogoToast.error(error)
            );
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const markApplicationAsScammer = async (waitlistApplication, defaultReason = '') => {
    const isScammerReason = window.prompt('Are you sure you want to mark this application as spam? If so, please provide a reason.', defaultReason);
    if (!isScammerReason) return;

    try {
      await updateWaitlistUser(waitlistApplication, { isScammer: true, isScammerReason, isAccepted: false, isRejected: true });
      cogoToast.success(`Marked ${waitlistApplication.name} as a scammer.`);
      closeModals();
    } catch (error) {
      cogoToast.error(error || 'Unknown error marking user as scammer.');
      console.error(error);
    }
  };

  const [automationStatus, setAutomationStatusRaw] = useState('Needs Manual Review');
  const [countryCode, setCountryCodeRaw] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [direction, setDirection] = useState('desc');

  const changeSort = (key, nextDirection) => {
    if (sort === key && direction === nextDirection) return;
    setSort(key);
    setDirection(nextDirection);
    setOffset(0);
    setWaitlist([]);
  };

  const setAutomationStatus = value => {
    setOffset(0);
    setWaitlist([]);
    setAutomationStatusRaw(value);
  };

  const setCountryCode = value => {
    setOffset(0);
    setWaitlist([]);
    setCountryCodeRaw(value);
  };
  const getResults = () => {
    if (curSearchValue && searchResults.length > 0) return searchResults;
    return waitlist;
  };

  const createManagerCode = () => createCode(true);
  const createCode = (isManager = false) => {
    const code = window.prompt('What code would you like to create?');
    if (!code) return;
    let codeObject = { code };

    if (isManager) {
      let ManagesUser_id;
      const managerIdResp = window.prompt('Would you like this manager to manage an existing User ID? If so, type it here.');
      if (managerIdResp) ManagesUser_id = parseInt(managerIdResp);

      // Build Object
      codeObject.useLimit = 3;
      codeObject.isManager = true;
      if (ManagesUser_id) codeObject.ManagesUser_id = ManagesUser_id;
    } else {
      // Number Uses
      const useLimitResp = window.prompt('How many uses would you like?', 3);
      const useLimit = parseInt(useLimitResp);
      if (!useLimit) return;

      // Specific Rate
      const rateResp = window.prompt('What % do you want for the user?', 82);
      const userPercentage = parseInt(rateResp);
      if (!userPercentage) return;

      codeObject.useLimit = useLimit;
      codeObject.userPercentage = userPercentage;
    }

    // Create the code
    waitlistAPI.createRegistrationCode(codeObject).then(
      () => cogoToast.success(`Created code ${code}${codeObject.ManagesUser_id ? ` to manage user ${codeObject.ManagesUser_id}` : ''}.`),
      () => cogoToast.error(`This code already exists.`)
    );
  };

  return (
    <div className='waitlist-outer-container'>
      <WaitlistData
        waitlist={waitlist}
        setWaitlist={setWaitlist}
        curSearchValue={curSearchValue}
        setSearchResults={setSearchResults}
        setIsLoading={setIsLoading}
        offset={offset}
        limit={limit}
        automationStatus={automationStatus}
        countryCode={countryCode}
        sort={sort}
        direction={direction}
      />

      <div className='waitlist-inner-container'>
        {!!selectedApplicationId && (
          <UserWaitlistDecisionModal
            waitlistUser={waitlist.find(u => u.id === selectedApplicationId) || searchResults.find(u => u.id === selectedApplicationId)}
            acceptUser={acceptUser}
            close={closeModals}
            updateWaitlistUser={updateWaitlistUser}
            rejectWaitlistApplication={rejectWaitlistApplication}
            markApplicationAsScammer={markApplicationAsScammer}
          />
        )}

        <div className='waitlist-title-container'>
          <h1>Waitlist</h1>

          <div className='waitlist-title-container-buttons'>
            <button onClick={() => setIsCountryBlocksModalOpen(true)} className='waitlist-button'>
              Manage Blocked Countries
            </button>
            <button onClick={() => setIsBypassCodesModalOpen(true)} className='waitlist-button'>
              Country Block Bypass Codes
            </button>
            <button onClick={() => createManagerCode()} className='waitlist-button'>
              Create Manager Code
            </button>
            <button onClick={() => createCode()} className='waitlist-button primary'>
              Create Code
            </button>
          </div>
        </div>

        {isCountryBlocksModalOpen && <WaitlistBlockedCountriesModal close={() => setIsCountryBlocksModalOpen(false)} />}
        {isBypassCodesModalOpen && <SignupCountryBlockBypassCodesModal close={() => setIsBypassCodesModalOpen(false)} />}

        <WaitlistControls
          automationStatus={automationStatus}
          setAutomationStatus={setAutomationStatus}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          curSearchValue={curSearchValue}
          setCurSearchValue={setCurSearchValue}
          resetSearch={resetSearch}
        />

        <div className={cn('results-container', { isSearching: isLoading && waitlist.length > 0 })}>
          <div className='waitlist-user row header'>
            {COLUMNS.map(column => (
              <div key={column.key} className={cn('cell', column.className)}>
                <div className='waitlist-sort'>
                  <span>{column.label}</span>
                  <span className='waitlist-sort-arrows'>
                    {SORT_DIRECTIONS.map(({ direction: dir, arrow }) => (
                      <Tooltip key={dir} message={column[dir]} position='bottom'>
                        <button
                          type='button'
                          className={cn({ active: sort === column.key && direction === dir })}
                          aria-label={`${column.label}: ${column[dir]}`}
                          onClick={() => changeSort(column.key, dir)}
                        >
                          {arrow}
                        </button>
                      </Tooltip>
                    ))}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {isLoading && waitlist.length === 0
            ? Array.from({ length: 8 }).map((_, i) => <WaitlistShimmerRow key={i} />)
            : getResults().length === 0
            ? <div className='waitlist-empty-state'>No applicants to review.</div>
            : getResults().map(waitlistUser => (
                <WaitlistUser
                  waitlistUser={waitlistUser}
                  setSelectedApplicationId={setSelectedApplicationId}
                  rejectWaitlistApplication={rejectWaitlistApplication}
                  markApplicationAsScammer={markApplicationAsScammer}
                  key={waitlistUser.id}
                />
              ))}

          {!isLoading && searchResults.length === 0 && getResults().length > 0 && (
            <button className='load-more-button' onClick={fetchMoreWaitlistUsers} disabled={isLoading}>
              Load More Users
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

Waitlist.propTypes = {};

export default Waitlist;
